import { useState, useEffect, useMemo, useRef } from 'react';
import { MdChevronLeft, MdChevronRight, MdCalendarToday, MdExpandMore } from 'react-icons/md';
import { useCalendar } from '../../context/CalendarContext';
import { adToBs, bsToAd, formatAd, formatBs, BS_MONTHS, getDaysInBsMonth } from '../../utils/calendar';
import { getNepalHoliday } from '../../data/nepalHolidays';

const AD_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Shared month-initial/year conversion helpers
function adMonthToBs(yearMonth) {
  // "YYYY-MM" → { year, month }
  const [y, m] = yearMonth.split('-').map(Number);
  const bs = adToBs(new Date(y, m - 1, 15)); // day 15 for mid-month BS estimate
  return { year: bs.year, month: bs.month };
}

function bsMonthToAd(bsYear, bsMonth) {
  // Returns AD "YYYY-MM" built from padded LOCAL Y/M (no toISOString) — matches the
  // existing value contract so Reports.getReportRange day-15 sampling stays correct.
  const ad = bsToAd(Number(bsYear), Number(bsMonth), 1);
  return `${ad.getFullYear()}-${String(ad.getMonth() + 1).padStart(2, '0')}`;
}

const pad2 = (n) => String(n).padStart(2, '0');

// ── Timezone-safe date <-> "YYYY-MM-DD" ───────────────────────────────────────
// Two traps this component kept falling into, both of which shift the day by one:
//   1. `dt.toISOString()` on a LOCAL-midnight Date. Asia/Kathmandu is UTC+5:45, so local
//      midnight is 18:15 the PREVIOUS day in UTC — clicking BS day 13 emitted day 12.
//   2. `new Date("YYYY-MM-DD")` parses as UTC midnight; reading it back with local getters
//      (getDate/adToBs) lands on the previous day in any timezone BEHIND UTC.
// Always go through these two helpers — never toISOString(), never new Date(str).
const isoLocal   = (dt) => `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
const parseLocal = (s) => {
  const [y, m, d] = String(s).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1); // local midnight — matches bsToAd()'s output
};

// One presentational month-grid used by BOTH AD and BS modes, so the two are
// guaranteed pixel-identical. Callers compute the weeks (each cell carries its own
// iso + flags) and pass the header label and nav/pick/today/clear handlers.
//
// Fast navigation (default ON): the header is tappable → a YEAR grid → tap a year → a MONTH
// grid → tap a month → back to the day grid. This is the native drill-down pattern, so
// reaching a distant birth year is 3 taps instead of hundreds of arrow clicks. Navigation only
// changes the VIEWED month/year (via onJumpTo*); the selected value never changes until a day
// is tapped. Arrows stay for fine stepping. Pass fastNav={false} to force the plain grid.
function CalendarGrid({
  weeks, monthLabel, onPrev, onNext, onPick, onToday, todayInRange, onClear, showClear,
  fastNav = true, monthNames, curMonth, curYear, years, onJumpToYear, onJumpToMonth,
}) {
  const canDrill = fastNav && Array.isArray(years) && Array.isArray(monthNames);
  const [view, setView] = useState('day'); // 'day' | 'year' | 'month'
  const yearGridRef = useRef(null);

  // Open the year grid scrolled to the currently-viewed year (centered), not the top of range.
  // rect-based so it's correct regardless of the grid's offset parent.
  useEffect(() => {
    if (view !== 'year' || !yearGridRef.current) return;
    const grid = yearGridRef.current;
    const cur  = grid.querySelector('[data-current="true"]');
    if (!cur) return;
    const delta = (cur.getBoundingClientRect().top - grid.getBoundingClientRect().top) - (grid.clientHeight / 2 - cur.clientHeight / 2);
    grid.scrollTop = Math.max(0, grid.scrollTop + delta);
  }, [view]);

  const arrowCls = 'w-9 h-9 flex items-center justify-center rounded-md text-muted hover:bg-nav-hover hover:text-secondary transition-colors';
  const headerBtnCls = 'inline-flex items-center gap-1 px-2 h-9 rounded-md text-sm font-semibold text-secondary hover:bg-nav-hover transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30';

  return (
    <div className="select-none">
      {/* Header row — arrows (day view only) + tappable drill-down title */}
      <div className="flex items-center justify-between mb-2">
        {view === 'day'
          ? <button type="button" onClick={onPrev} aria-label="Previous month" className={arrowCls}><MdChevronLeft className="text-lg" /></button>
          : <span className="w-9 h-9" />}

        {canDrill ? (
          view === 'day' ? (
            <button type="button" onClick={() => setView('year')} className={headerBtnCls} aria-label="Choose year and month">
              {monthNames[curMonth - 1]} {curYear} <MdExpandMore className="text-base" />
            </button>
          ) : view === 'year' ? (
            <button type="button" onClick={() => setView('day')} className={headerBtnCls}>Select year</button>
          ) : (
            <button type="button" onClick={() => setView('year')} className={headerBtnCls}>
              <MdChevronLeft className="text-base" /> {curYear}
            </button>
          )
        ) : (
          <span className="text-sm font-semibold text-secondary">{monthLabel}</span>
        )}

        {view === 'day'
          ? <button type="button" onClick={onNext} aria-label="Next month" className={arrowCls}><MdChevronRight className="text-lg" /></button>
          : <span className="w-9 h-9" />}
      </div>

      {canDrill && view === 'year' ? (
        /* YEAR grid — scrollable, descending (DOB-friendly), current year highlighted */
        <div ref={yearGridRef} className="grid grid-cols-4 gap-1.5 max-h-[220px] overflow-y-auto py-0.5">
          {years.map(y => (
            <button
              key={y}
              type="button"
              data-current={y === curYear ? 'true' : undefined}
              onClick={() => { onJumpToYear(y); setView('month'); }}
              className={`min-h-[44px] rounded-lg text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 ${
                y === curYear ? 'bg-primary text-white' : 'bg-nav-hover/60 text-secondary hover:bg-nav-hover'
              }`}
            >
              {y}
            </button>
          ))}
        </div>
      ) : canDrill && view === 'month' ? (
        /* MONTH grid — 12 months in the active calendar (AD or BS) */
        <div className="grid grid-cols-3 gap-1.5 py-0.5">
          {monthNames.map((nm, i) => {
            const mi = i + 1;
            return (
              <button
                key={nm}
                type="button"
                onClick={() => { onJumpToMonth(mi); setView('day'); }}
                className={`min-h-[44px] rounded-lg text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 ${
                  mi === curMonth ? 'bg-primary text-white' : 'bg-nav-hover/60 text-secondary hover:bg-nav-hover'
                }`}
              >
                {nm}
              </button>
            );
          })}
        </div>
      ) : (
        <>
          {/* Weekday header (Sun–Sat) */}
          <div className="grid grid-cols-7 mb-1">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((w, i) => (
              <div key={w} className={`text-center text-[11px] font-medium ${i === 6 ? 'text-red-500' : 'text-muted'}`}>{w}</div>
            ))}
          </div>

          {/* Day cells — gapless grid so every pixel belongs to exactly one cell
              (no 2px dead zones that route an edge tap to the neighbor), and each
              button fills its whole 1fr column so the clickable box === the visible box. */}
          <div className="grid grid-cols-7">
            {weeks.flat().map((cell, idx) => {
              if (!cell) return <div key={idx} />;
              const isRed    = cell.isSat || cell.holiday;
              const markToday = cell.today && !cell.selected && !cell.disabled;
              return (
                <button
                  key={idx}
                  type="button"
                  disabled={cell.disabled}
                  onClick={() => onPick(cell)}
                  title={cell.today ? 'Today' : (cell.holiday || undefined)}
                  aria-current={cell.today ? 'date' : undefined}
                  className={`relative w-full h-10 rounded-md text-sm flex items-center justify-center transition-colors ${
                    cell.selected ? 'bg-primary text-white font-semibold'
                      : cell.disabled ? 'text-subtle opacity-40 cursor-not-allowed'
                      : cell.today ? 'ring-1 ring-primary text-secondary font-medium hover:bg-nav-hover'
                      : isRed ? 'text-red-500 hover:bg-nav-hover'
                      : 'text-secondary hover:bg-nav-hover'
                  }`}
                >
                  {cell.d}
                  {/* Subtle "today" dot under the number — distinct from the filled "selected" state. */}
                  {markToday && (
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" />
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Today / Clear — day view only (irrelevant while picking a year/month) */}
      {view === 'day' && (
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-divider">
          <button
            type="button" onClick={onToday} disabled={!todayInRange}
            className="text-xs font-medium text-primary hover:underline disabled:opacity-40 disabled:no-underline"
          >
            Today
          </button>
          {showClear && (
            <button
              type="button" onClick={onClear}
              className="text-xs font-medium text-muted hover:text-secondary transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── MonthPopover (monthOnly mode) ──────────────────────────────────────────────
// Premium dark month picker: a trigger button + an anchored popover with a LOCAL BS/AD
// toggle, year nav, and a 3×4 month grid. Value contract is unchanged: accepts an AD
// "YYYY-MM" string and emits AD "YYYY-MM". A BS pick converts via bsToAd(y, m, 1) → that
// AD year-month (literal padded Y/M, no UTC), so Reports' range logic stays correct.
function MonthPopover({ value, onChange, min, max, label, required, error, className = '' }) {
  const { currentCalendar } = useCalendar();
  const [mode, setMode]           = useState(currentCalendar);
  const [open, setOpen]           = useState(false);
  const [viewYear, setViewYear]   = useState(null); // null → derive from selection/today
  const [alignRight, setAlignRight] = useState(false);
  const rootRef = useRef(null);

  // Follow the global club calendar if it changes (matches prior behavior). The local
  // BS/AD toggle still overrides within this picker only.
  useEffect(() => { setMode(currentCalendar); }, [currentCalendar]);
  // Re-center the displayed year on the selection whenever value or mode changes.
  useEffect(() => { setViewYear(null); }, [value, mode]);

  // Close on outside-click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  // Kathmandu-local "today" (UTC+5:45) in each calendar — no toISOString.
  const kthNow = new Date(Date.now() + (5 * 60 + 45) * 60 * 1000);
  const tAd = { year: kthNow.getUTCFullYear(), month: kthNow.getUTCMonth() + 1 };
  const tBs = (() => {
    const bs = adToBs(new Date(kthNow.getUTCFullYear(), kthNow.getUTCMonth(), kthNow.getUTCDate()));
    return { year: bs.year, month: bs.month };
  })();

  // Min/max as comparable "YYYY-MM" bounds (accept "YYYY-MM" or "YYYY-MM-DD").
  const minYM = min ? min.slice(0, 7) : null;
  const maxYM = max ? max.slice(0, 7) : null;
  const inRange = (ym) => !((minYM && ym < minYM) || (maxYM && ym > maxYM));

  // The stored AD value interpreted in the active display calendar (for highlight/label).
  const sel = (() => {
    if (!value) return null;
    try {
      if (mode === 'bs') { const bs = adMonthToBs(value); return { year: bs.year, month: bs.month }; }
      const [y, m] = value.split('-').map(Number);
      return { year: y, month: m };
    } catch { return null; }
  })();

  const todayInMode = mode === 'bs' ? tBs : tAd;
  const effYear = viewYear != null ? viewYear : (sel?.year ?? todayInMode.year);
  const months  = mode === 'bs' ? BS_MONTHS : AD_MONTHS;

  // Each month cell → its AD "YYYY-MM" (BS via bsToAd day-1, AD literal).
  const candidateYM = (mi) => (mode === 'bs' ? bsMonthToAd(effYear, mi) : `${effYear}-${pad2(mi)}`);

  const triggerLabel = (() => {
    if (!value) return 'Select month';
    try {
      if (mode === 'bs') { const bs = adMonthToBs(value); return `${BS_MONTHS[bs.month - 1]} ${bs.year}`; }
      const [y, m] = value.split('-').map(Number);
      return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    } catch { return 'Select month'; }
  })();

  const otherYear = mode === 'bs'
    ? `${bsToAd(effYear, 6, 1).getFullYear()} AD`
    : `${adToBs(new Date(effYear, 5, 15)).year} BS`;

  const openPopover = () => {
    const r = rootRef.current?.getBoundingClientRect();
    if (r) setAlignRight(r.left + 300 > window.innerWidth - 8); // flip to right-align near the edge
    setOpen(true);
  };

  const pick = (mi) => {
    const ym = candidateYM(mi);
    if (!inRange(ym)) return;
    onChange(ym);
    setOpen(false);
  };

  const goThisMonth = () => {
    const ym = mode === 'bs' ? bsMonthToAd(tBs.year, tBs.month) : `${tAd.year}-${pad2(tAd.month)}`;
    if (!inRange(ym)) return;
    setViewYear(null);
    onChange(ym);
    setOpen(false);
  };

  return (
    <div className={`relative inline-block ${className}`} ref={rootRef}>
      {label && (
        <label className="block text-xs font-medium text-muted mb-1">
          {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
      )}

      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openPopover())}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="h-10 inline-flex items-center gap-2 px-3 rounded-lg border border-divider bg-card text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 transition-colors"
      >
        <MdCalendarToday className="text-muted text-base flex-shrink-0" />
        <span className={value ? 'text-secondary' : 'text-muted'}>{triggerLabel}</span>
        <MdExpandMore className={`text-muted text-lg flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="dialog"
          className={`absolute top-full mt-1 z-50 w-[300px] max-w-[calc(100vw-1rem)] bg-card border border-divider rounded-xl shadow-xl p-4 ${alignRight ? 'right-0' : 'left-0'}`}
        >
          {/* BS / AD toggle — local to this picker (does NOT change the global calendar) */}
          <div className="flex items-center gap-1 bg-nav-hover/40 rounded-lg p-1">
            {['bs', 'ad'].map(md => (
              <button
                key={md}
                type="button"
                onClick={() => setMode(md)}
                className={`flex-1 h-7 rounded-md text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 ${
                  mode === md ? 'bg-primary text-white' : 'text-muted hover:text-secondary'
                }`}
              >
                {md.toUpperCase()}
              </button>
            ))}
          </div>

          {/* Year nav */}
          <div className="flex items-center justify-between mt-3">
            <button
              type="button" onClick={() => setViewYear(effYear - 1)} aria-label="Previous year"
              className="w-9 h-9 flex items-center justify-center rounded-md text-muted hover:bg-nav-hover hover:text-secondary transition-colors"
            >
              <MdChevronLeft className="text-lg" />
            </button>
            <div className="text-center">
              <p className="text-sm font-semibold text-secondary leading-none">{mode === 'bs' ? `${effYear} BS` : effYear}</p>
              <p className="text-[10px] text-subtle mt-1">≈ {otherYear}</p>
            </div>
            <button
              type="button" onClick={() => setViewYear(effYear + 1)} aria-label="Next year"
              className="w-9 h-9 flex items-center justify-center rounded-md text-muted hover:bg-nav-hover hover:text-secondary transition-colors"
            >
              <MdChevronRight className="text-lg" />
            </button>
          </div>

          {/* Month grid (3 × 4) */}
          <div className="grid grid-cols-3 gap-1.5 mt-3">
            {months.map((name, i) => {
              const mi = i + 1;
              const disabled = !inRange(candidateYM(mi));
              const selected = sel && sel.year === effYear && sel.month === mi;
              return (
                <button
                  key={name}
                  type="button"
                  disabled={disabled}
                  onClick={() => pick(mi)}
                  className={`h-9 rounded-lg text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 ${
                    selected ? 'bg-primary text-white'
                      : disabled ? 'text-subtle opacity-40 cursor-not-allowed'
                      : 'bg-nav-hover/60 text-secondary hover:bg-nav-hover'
                  }`}
                >
                  {name}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-divider">
            <button
              type="button" onClick={goThisMonth}
              className="text-xs font-medium text-primary hover:underline"
            >
              This month
            </button>
            <button
              type="button" onClick={() => setOpen(false)}
              className="text-xs font-medium text-muted hover:text-secondary transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-xs text-red-500 mt-1 leading-none">{error}</p>}
    </div>
  );
}

// ── DualDatePicker ────────────────────────────────────────────────────────────
// Props:
//   value      — ISO date "YYYY-MM-DD" (date mode) or "YYYY-MM" (monthOnly mode)
//   onChange   — called with same format
//   monthOnly  — boolean; renders the MonthPopover (premium month picker)
//   label, required, min, max, error, compact, className
function DualDatePicker({ value, onChange, label, required, min, max, error, monthOnly = false, compact = false, className = '', fastNav = true, defaultMode }) {
  const { currentCalendar } = useCalendar();
  // defaultMode (e.g. 'bs' on the invite pages) wins over the global preference.
  const [mode, setMode] = useState(defaultMode || currentCalendar);

  // "Today" in Asia/Kathmandu (UTC+5:45) — never the browser/UTC calendar day. Mirrors MonthPopover.
  const kthNow    = new Date(Date.now() + (5 * 60 + 45) * 60 * 1000);
  const todayAd   = { y: kthNow.getUTCFullYear(), m: kthNow.getUTCMonth() + 1, d: kthNow.getUTCDate() };
  const todayBs   = adToBs(new Date(kthNow.getUTCFullYear(), kthNow.getUTCMonth(), kthNow.getUTCDate()));

  // Calendar-grid view month — kept per-calendar (BS units vs Gregorian units) so the
  // active grid shows the right month; both auto-sync to the selected value below.
  const [viewYear,  setViewYear]  = useState(todayBs.year);
  const [viewMonth, setViewMonth] = useState(todayBs.month);
  const [adView,    setAdView]    = useState({ y: todayAd.y, m: todayAd.m });

  // Sync mode when global preference changes — unless a defaultMode override was given.
  useEffect(() => { if (!defaultMode) setMode(currentCalendar); }, [currentCalendar, defaultMode]);

  // Jump both grids to the selected date's month whenever the value changes.
  useEffect(() => {
    if (monthOnly || !value) return;
    try { const bs = adToBs(parseLocal(value)); setViewYear(bs.year); setViewMonth(bs.month); }
    catch { /* keep current view */ }
    const p = value.split('-');
    if (p.length >= 2) setAdView({ y: Number(p[0]), m: Number(p[1]) });
  }, [value, monthOnly]);

  const tBsY = todayBs.year, tBsM = todayBs.month, tBsD = todayBs.day;
  const tAdY = todayAd.y,    tAdM = todayAd.m,     tAdD = todayAd.d;

  // ── Calendar-grid data (one shared renderer; only the month math differs) ──
  const bsWeeks = useMemo(() => {
    if (monthOnly) return [];
    const count  = getDaysInBsMonth(viewYear, viewMonth);
    const offset = bsToAd(viewYear, viewMonth, 1).getDay(); // Sun-start: Sun→0
    let sel = null;
    if (value) { try { sel = adToBs(parseLocal(value)); } catch { sel = null; } }
    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= count; d++) {
      const ad  = bsToAd(viewYear, viewMonth, d);
      const iso = isoLocal(ad);
      cells.push({
        d, iso,
        isSat:    ad.getDay() === 6,
        holiday:  getNepalHoliday(viewYear, viewMonth, d),
        disabled: Boolean((min && iso < min) || (max && iso > max)),
        selected: !!sel && sel.year === viewYear && sel.month === viewMonth && sel.day === d,
        today:    tBsY === viewYear && tBsM === viewMonth && tBsD === d,
      });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
  }, [monthOnly, viewYear, viewMonth, min, max, value, tBsY, tBsM, tBsD]);

  const adWeeks = useMemo(() => {
    if (monthOnly) return [];
    const { y, m } = adView;
    const count  = new Date(y, m, 0).getDate();    // days in month m (1-12)
    const offset = new Date(y, m - 1, 1).getDay(); // Sun-start: Sun→0
    const p   = value ? value.split('-') : null;
    const sel = (p && p.length >= 3) ? { y: +p[0], m: +p[1], d: +p[2] } : null;
    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= count; d++) {
      const iso = `${y}-${pad2(m)}-${pad2(d)}`;
      cells.push({
        d, iso,
        isSat:    new Date(y, m - 1, d).getDay() === 6,
        holiday:  null, // AD grid marks only Saturday (weekend) red — matches BS weekend; no AD holiday data
        disabled: Boolean((min && iso < min) || (max && iso > max)),
        selected: !!sel && sel.y === y && sel.m === m && sel.d === d,
        today:    tAdY === y && tAdM === m && tAdD === d,
      });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
  }, [monthOnly, adView, min, max, value, tAdY, tAdM, tAdD]);

  // monthOnly is a self-contained popover with the same AD "YYYY-MM" value contract.
  // (Day-level rendering below is untouched.)
  if (monthOnly) {
    return (
      <MonthPopover
        value={value}
        onChange={onChange}
        min={min}
        max={max}
        label={label}
        required={required}
        error={error}
        className={className}
      />
    );
  }

  const isoFromBs = (y, m, d) => isoLocal(bsToAd(y, m, d));

  // Navigation
  const goPrevMonth = () => { if (viewMonth === 1) { setViewMonth(12); setViewYear(y => y - 1); } else setViewMonth(m => m - 1); };
  const goNextMonth = () => { if (viewMonth === 12) { setViewMonth(1); setViewYear(y => y + 1); } else setViewMonth(m => m + 1); };
  const adPrev = () => setAdView(v => (v.m === 1  ? { y: v.y - 1, m: 12 } : { y: v.y, m: v.m - 1 }));
  const adNext = () => setAdView(v => (v.m === 12 ? { y: v.y + 1, m: 1  } : { y: v.y, m: v.m + 1 }));

  // Fast-nav year lists for the drill-down grid. Descending (DOB-friendly): from a sensible
  // top (current+2, so near-future dates like next year's exam are reachable) down ~90 years.
  // The range is clamped to any min/max so the year grid can never offer a fully-disallowed
  // year — the day grid still disables individual out-of-range days as a second guard.
  const FAST_BACK = 90, FAST_FWD = 2;
  const yearOfIso = (iso, fallback) => { const n = Number(String(iso).slice(0, 4)); return Number.isFinite(n) ? n : fallback; };
  const bsYearOfIso = (iso, fallback) => { try { return adToBs(parseLocal(iso)).year; } catch { return fallback; } };
  const descend = (top, bot) => { const out = []; for (let y = top; y >= bot; y--) out.push(y); return out; };

  const adTop   = Math.min(tAdY + FAST_FWD, max ? yearOfIso(max, tAdY + FAST_FWD) : tAdY + FAST_FWD);
  const adBot   = Math.max(tAdY - FAST_BACK, min ? yearOfIso(min, tAdY - FAST_BACK) : tAdY - FAST_BACK);
  const adYears = descend(adTop, Math.min(adBot, adTop));

  const bsTop   = Math.min(tBsY + FAST_FWD, max ? bsYearOfIso(max, tBsY + FAST_FWD) : tBsY + FAST_FWD);
  const bsBot   = Math.max(tBsY - FAST_BACK, min ? bsYearOfIso(min, tBsY - FAST_BACK) : tBsY - FAST_BACK);
  const bsYears = descend(bsTop, Math.min(bsBot, bsTop));

  // "Today" is a quick-jump only — it navigates the grid to the current month WITHOUT
  // selecting the date (the user still taps the day). Disabled if today is out of range.
  const bsTodayIso     = isoFromBs(tBsY, tBsM, tBsD);
  const bsTodayInRange = !((min && bsTodayIso < min) || (max && bsTodayIso > max));
  const bsGoToday = () => { if (!bsTodayInRange) return; setViewYear(tBsY); setViewMonth(tBsM); };

  const adTodayIso     = `${tAdY}-${pad2(tAdM)}-${pad2(tAdD)}`;
  const adTodayInRange = !((min && adTodayIso < min) || (max && adTodayIso > max));
  const adGoToday = () => { if (!adTodayInRange) return; setAdView({ y: tAdY, m: tAdM }); };

  const adMonthLabel = new Date(adView.y, adView.m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  // Hint: other format
  let hint = '';
  if (value) {
    try {
      hint = mode === 'bs' ? `≈ ${formatAd(parseLocal(value))}` : `≈ ${formatBs(parseLocal(value))}`;
    } catch { hint = ''; }
  }

  // If the selected day is an official Nepali public holiday, surface its name.
  let holidayName = null;
  if (value) {
    try {
      const bs = adToBs(parseLocal(value));
      holidayName = getNepalHoliday(bs.year, bs.month, bs.day);
    } catch { holidayName = null; }
  }

  return (
    <div className={`flex flex-col ${compact ? 'gap-2' : 'gap-1'} ${className}`}>
      {/* Header — the BS/AD toggle always shows (even without a label), so every consumer
          (incl. the invite DOB pickers) exposes the calendar switch. */}
      <div className="flex items-center justify-between">
        {label ? (
          <label className="text-xs font-medium text-gray-600">
            {label}{required && <span className="text-red-400 ml-0.5">*</span>}
          </label>
        ) : <span />}
        <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden text-xs">
          <button
            type="button"
            onClick={() => setMode('ad')}
            className={`px-2.5 py-1 transition-colors ${mode === 'ad' ? 'bg-primary text-white font-semibold' : 'text-gray-400 hover:text-gray-600'}`}
          >AD</button>
          <button
            type="button"
            onClick={() => setMode('bs')}
            className={`px-2.5 py-1 transition-colors ${mode === 'bs' ? 'bg-primary text-white font-semibold' : 'text-gray-400 hover:text-gray-600'}`}
          >BS</button>
        </div>
      </div>

      {mode === 'ad' ? (
        <CalendarGrid
          weeks={adWeeks}
          monthLabel={adMonthLabel}
          onPrev={adPrev}
          onNext={adNext}
          onPick={(c) => onChange(c.iso)}
          onToday={adGoToday}
          todayInRange={adTodayInRange}
          onClear={() => onChange('')}
          showClear={!!value}
          fastNav={fastNav}
          monthNames={AD_MONTHS}
          curMonth={adView.m}
          curYear={adView.y}
          years={adYears}
          onJumpToYear={(y) => setAdView(v => ({ y, m: v.m }))}
          onJumpToMonth={(m) => setAdView(v => ({ y: v.y, m }))}
        />
      ) : (
        <CalendarGrid
          weeks={bsWeeks}
          monthLabel={`${BS_MONTHS[viewMonth - 1]} ${viewYear}`}
          onPrev={goPrevMonth}
          onNext={goNextMonth}
          onPick={(c) => onChange(c.iso)}
          onToday={bsGoToday}
          todayInRange={bsTodayInRange}
          onClear={() => onChange('')}
          showClear={!!value}
          fastNav={fastNav}
          monthNames={BS_MONTHS}
          curMonth={viewMonth}
          curYear={viewYear}
          years={bsYears}
          onJumpToYear={(y) => setViewYear(y)}
          onJumpToMonth={(m) => setViewMonth(m)}
        />
      )}

      {hint && (
        <p className="text-xs text-gray-400 leading-none">
          {hint}
          {holidayName && <span className="text-red-400"> · {holidayName}</span>}
        </p>
      )}
      {error && <p className="text-xs text-red-500 leading-none">{error}</p>}
    </div>
  );
}

export default DualDatePicker;
